import { CALCULATION_PROCESS_START } from 'actions/designer'
import {
  TARIFF_RECOMMENDATION_CLEAR,
  TARIFF_RECOMMENDATION_FAILURE,
  TARIFF_RECOMMENDATION_REQUEST,
  TARIFF_RECOMMENDATION_START,
  TARIFF_RECOMMENDATION_SUCCESS,
  TariffRecommendationRequestAction,
} from 'actions/designer/tariffRecommendationActions'
import { getTariffRecommendations } from 'api/tariffRecommendationsApi'
import { authSelectors } from 'ducks/auth'
import { MARK_SYSTEM_CALC_AS_COMPLETE } from 'ducks/paymentOptionSelection'
import { sponsoredTariffsSelectors } from 'ducks/sponsoredTariffs'
import { Logger } from 'opensolar-sdk'
import { hasProposedTariff } from 'projectSections/sections/energy/ElectricityUtility/hooks'
import { call, put, select, takeEvery } from 'redux-saga/effects'
import { urlToId } from 'util/misc'

const logger = new Logger('OS.TariffRecommendations')


function extractUtilityId(
  tariffRecord?: any,
  tariffData?: any,
  proposedTariffData?: any
): number | undefined {
  if (proposedTariffData?.utility) {
    try {
      const id = urlToId(proposedTariffData.utility)
      if (id) return id
    } catch {
    }
  }

  if (tariffRecord?.utility) {
    try {
      const id = urlToId(tariffRecord.utility)
      if (id) return id
    } catch {
    }
  }

  return undefined
}

function getSystemWithResults(system_uuid: string) {
  const system = window.editor?.objectByUuid?.(system_uuid)
  if (!system) {
    return null
  }
  if (!system.output || !system.pricing) {
    return null
  }
  return system
}

function isTariffSponsored(
  tariff: any,
  sponsoredTariffCodes: string[]
): boolean {
  if (tariff.tariffGroupCode) {
    return sponsoredTariffCodes.includes(tariff.tariffGroupCode)
  }
  if (tariff.code) {
    return sponsoredTariffCodes.includes(tariff.code)
  }
  return false
}

function checkSponsoredTariffAndExtractUtility(
  tariff: any,
  sponsoredTariffCodes: string[],
  logContext: Record<string, any>,
  logMessage: string,
  warnMessage: string
): { shouldFetch: boolean; utilityId?: number } | null {
  if (!tariff || !isTariffSponsored(tariff, sponsoredTariffCodes)) {
    return null
  }

  const utilityId = extractUtilityId(
    tariff.tariffRecord,
    tariff.tariffData
  )

  if (!utilityId) {
    logger.warn(warnMessage, {
      tariff_id: tariff.id,
      ...logContext,
    })
    return null
  }

  logger.log(logMessage, {
    utility_id: utilityId,
    ...logContext,
  })

  return { shouldFetch: true, utilityId }
}

function checkSystemOverrideTariff(
  system: any,
  hydratedTariffs: any[],
  sponsoredTariffCodes: string[]
): { shouldFetch: boolean; utilityId?: number } | null {
  if (!system?.utility_tariff_id_override) {
    return null
  }

  const overrideTariff = hydratedTariffs.find(
    (tariff) => tariff.id === system.utility_tariff_id_override
  )

  return checkSponsoredTariffAndExtractUtility(
    overrideTariff,
    sponsoredTariffCodes,
    {
      system_uuid: system.uuid,
      tariff_override_id: system.utility_tariff_id_override,
    },
    'System has sponsored tariff override - filtering by utility',
    'No utility_id found in override tariff'
  )
}

function checkProposedTariff(
  projectFormValues: any,
  hydratedTariffs: any[],
  sponsoredTariffCodes: string[]
): { shouldFetch: boolean; utilityId?: number } | null {
  if (!projectFormValues || !hasProposedTariff(projectFormValues)) {
    return null
  }

  const proposedTariffData = projectFormValues?.utility_tariff_proposed_data
  const proposedTariffGroupCode = proposedTariffData?.utility_tariff_group__code

  if (!proposedTariffGroupCode || !sponsoredTariffCodes.includes(proposedTariffGroupCode)) {
    return null
  }

  const utilityIdFromForm = extractUtilityId(
    undefined,
    undefined,
    proposedTariffData
  )

  if (utilityIdFromForm) {
    logger.log('Project has sponsored tariff selected - filtering by utility', {
      tariff_group_code: proposedTariffGroupCode,
      utility_id: utilityIdFromForm,
    })
    return { shouldFetch: true, utilityId: utilityIdFromForm }
  }

  const proposedTariff = hydratedTariffs.find(
    (tariff) => tariff.tariffGroupCode === proposedTariffGroupCode || tariff.code === proposedTariffGroupCode
  )

  return checkSponsoredTariffAndExtractUtility(
    proposedTariff,
    sponsoredTariffCodes,
    {
      tariff_group_code: proposedTariffGroupCode,
    },
    'Project has sponsored tariff selected - filtering by utility',
    'No utility_id found in proposed tariff'
  )
}

function* shouldFetchRecommendations(system: any, projectFormValues: any): Generator<any, { shouldFetch: boolean; utilityId?: number }, any> {
  const orgId = yield select(authSelectors.getOrgId)
  if (!orgId) {
    logger.warn('Cannot fetch tariff recommendations: orgId not available')
    return { shouldFetch: false }
  }

  const sponsoredTariffCodes = yield select(sponsoredTariffsSelectors.getTariffCodes)
  const hydratedTariffs = yield select(sponsoredTariffsSelectors.getHydratedTariffs)

  const systemOverrideResult = checkSystemOverrideTariff(
    system,
    hydratedTariffs,
    sponsoredTariffCodes
  )
  if (systemOverrideResult) {
    return systemOverrideResult
  }

  // Check for sponsored tariff in project form values
  const proposedTariffResult = checkProposedTariff(
    projectFormValues,
    hydratedTariffs,
    sponsoredTariffCodes
  )
  if (proposedTariffResult) {
    return proposedTariffResult
  }

  return { shouldFetch: true }
}


function* fetchTariffRecommendations(action: TariffRecommendationRequestAction): Generator<any, void, any> {
  const { system_uuid } = action.payload

  try {
    const sponsoredTariffsEnabled = window.isFeatureEnabled?.('enable_sponsored_tariffs', 'on')
    const recommendationEngineEnabled = window.isFeatureEnabled?.('enable_tariff_recommendation_engine', 'on')
    
    if (!sponsoredTariffsEnabled || !recommendationEngineEnabled) {
      logger.log('Skipping tariff recommendations: feature flags disabled', { 
        system_uuid,
        sponsored_tariffs_enabled: sponsoredTariffsEnabled,
        recommendation_engine_enabled: recommendationEngineEnabled,
      })
      yield put({
        type: TARIFF_RECOMMENDATION_CLEAR,
        payload: { system_uuid },
      })
      return
    }

    const system = getSystemWithResults(system_uuid)
    if (!system) {
      logger.warn('Cannot fetch tariff recommendations: system not found or results not available', {
        system_uuid,
        has_system: !!window.editor?.objectByUuid?.(system_uuid),
      })
      yield put({
        type: TARIFF_RECOMMENDATION_CLEAR,
        payload: { system_uuid },
      })
      return
    }

    const projectFormValues = window.projectForm?.getState()?.values
    const { shouldFetch, utilityId: requiredUtilityId } = yield* shouldFetchRecommendations(system, projectFormValues)
    
    if (!shouldFetch) {
      yield put({
        type: TARIFF_RECOMMENDATION_CLEAR,
        payload: { system_uuid },
      })
      return
    }

    const sponsoredTariffCodes = yield select(sponsoredTariffsSelectors.getTariffCodes)
    const hydratedTariffs = yield select(sponsoredTariffsSelectors.getHydratedTariffs)
    if (sponsoredTariffCodes.length === 0) {
      yield put({
        type: TARIFF_RECOMMENDATION_CLEAR,
        payload: { system_uuid },
      })
      return
    }

    const hasBatteries = system.batteries && system.batteries().length > 0

    const tariffClassifications: Record<string, 'battery' | 'solar'> = {}
    hydratedTariffs.forEach((tariff) => {
      if (tariff.code && tariff.classification) {
        tariffClassifications[tariff.code] = tariff.classification
      }
    })

    let filteredTariffCodes = sponsoredTariffCodes
    let filteredTariffClassifications = tariffClassifications

    if (hasBatteries) {
      filteredTariffCodes = sponsoredTariffCodes.filter((code) => {
        return tariffClassifications[code] === 'battery'
      })
      filteredTariffClassifications = Object.fromEntries(
        Object.entries(tariffClassifications).filter(([code]) => filteredTariffCodes.includes(code))
      ) as Record<string, 'battery' | 'solar'>

      if (filteredTariffCodes.length === 0) {
        logger.log('No battery-classified tariffs available for system with batteries', { system_uuid })
        yield put({
          type: TARIFF_RECOMMENDATION_CLEAR,
          payload: { system_uuid },
        })
        return
      }
    }

    const orgId = yield select(authSelectors.getOrgId)
    const projectId = projectFormValues?.id
    const designData = window.editor?.sceneAsJSON()

    if (!designData) {
      throw new Error('Design data not available')
    }

    yield put({
      type: TARIFF_RECOMMENDATION_START,
      payload: { system_uuid },
    })

    const filters: { utility_id?: number } = {}
    if (requiredUtilityId) {
      filters.utility_id = requiredUtilityId
    }

    // Extract unsaved tariff codes if form has unsaved tariff data
    const unsavedTariffCurrentCode = projectFormValues?.utility_tariff_current_data?.code || 
      projectFormValues?.utility_tariff_current_custom?.code || 
      projectFormValues?.utility_tariff_current_data?.utility_tariff_group__code
    const unsavedTariffProposedCode = projectFormValues?.utility_tariff_proposed_data?.code || 
      projectFormValues?.utility_tariff_proposed_custom?.code || 
      projectFormValues?.utility_tariff_proposed_data?.utility_tariff_group__code

    logger.log('Fetching tariff recommendations', {
      tariff_codes: filteredTariffCodes,
      tariff_classifications: filteredTariffClassifications,
      has_batteries: hasBatteries,
      filters,
      system_uuid,
      unsaved_tariff_current_code: unsavedTariffCurrentCode,
      unsaved_tariff_proposed_code: unsavedTariffProposedCode,
    })

    const response = yield call(getTariffRecommendations, orgId, projectId, {
      design_data: designData,
      tariff_codes: filteredTariffCodes,
      system_uuid,
      tariff_classifications: Object.keys(filteredTariffClassifications).length > 0 ? filteredTariffClassifications : undefined,
      filters: Object.keys(filters).length > 0 ? filters : undefined,
      unsaved_tariff_current_code: unsavedTariffCurrentCode,
      unsaved_tariff_proposed_code: unsavedTariffProposedCode,
    })

    if (!response.recommendations || response.recommendations.length === 0) {
      logger.log('No tariff recommendations with additional savings', {
        system_uuid,
        baseline_savings: response.baseline_tariff_savings,
      })
      yield put({
        type: TARIFF_RECOMMENDATION_CLEAR,
        payload: { system_uuid },
      })
      return
    }

    logger.log('Tariff recommendations fetched successfully', {
      total_tariffs_evaluated: response.total_tariffs_evaluated,
      recommendations_count: response.recommendations.length,
      baseline_savings: response.baseline_tariff_savings,
    })

    yield put({
      type: TARIFF_RECOMMENDATION_SUCCESS,
      payload: { system_uuid, response },
    })
  } catch (error: any) {
    logger.error('Failed to fetch tariff recommendations', error)
    yield put({
      type: TARIFF_RECOMMENDATION_FAILURE,
      payload: { system_uuid, error: error.message || 'Unknown error' },
    })
  }
}


function* handleCalculationComplete(action: any): Generator<any, void, any> {
  const system_uuid = action.payload?.uuid
  if (!system_uuid) {
    return
  }

  yield put({
    type: TARIFF_RECOMMENDATION_REQUEST,
    payload: { system_uuid },
  })
}


function* handleCalculationStart(action: any): Generator<any, void, any> {
  // Don't clear recommendations on calculation start - keep them visible as skeleton
  // If we wish to perform some action here, such as clearing in the future we can.
}

export function* watchTariffRecommendationRequest() {
  yield takeEvery(TARIFF_RECOMMENDATION_REQUEST, fetchTariffRecommendations)
}

export function* watchCalculationCompleteForTariffRecommendations() {
  yield takeEvery(MARK_SYSTEM_CALC_AS_COMPLETE, handleCalculationComplete)
}

export function* watchCalculationStartForTariffRecommendations() {
  yield takeEvery(CALCULATION_PROCESS_START, handleCalculationStart)
}
